import { type ReactNode } from "react";
import {
  ShopifyProvider as HydrogenShopifyProvider,
  CartProvider,
} from "@shopify/hydrogen-react";

interface Props {
  children: ReactNode;
}

const storeDomain = import.meta.env.VITE_SHOPIFY_STORE_DOMAIN ?? "geoforge3d.myshopify.com";
const storefrontToken = import.meta.env.VITE_SHOPIFY_STOREFRONT_TOKEN ?? "";
const storefrontApiVersion = "2026-01";

// Custom cart fragment: same as hydrogen-react default but without
// buyerIdentity.customer (requires unauthenticated_read_customers scope
// which our Storefront token doesn't have).
const CART_FRAGMENT = `
  fragment CartFragment on Cart {
    id
    checkoutUrl
    totalQuantity
    buyerIdentity {
      countryCode
      email
      phone
    }
    lines(first: $numCartLines) {
      edges {
        node {
          id
          quantity
          attributes {
            key
            value
          }
          cost {
            totalAmount {
              amount
              currencyCode
            }
            compareAtAmountPerQuantity {
              amount
              currencyCode
            }
          }
          merchandise {
            ... on ProductVariant {
              id
              availableForSale
              compareAtPrice {
                ...MoneyFragment
              }
              price {
                ...MoneyFragment
              }
              requiresShipping
              title
              image {
                ...ImageFragment
              }
              product {
                handle
                title
                id
              }
              selectedOptions {
                name
                value
              }
            }
          }
        }
      }
    }
    cost {
      subtotalAmount {
        ...MoneyFragment
      }
      totalAmount {
        ...MoneyFragment
      }
      totalDutyAmount {
        ...MoneyFragment
      }
      totalTaxAmount {
        ...MoneyFragment
      }
    }
    note
    attributes {
      key
      value
    }
    discountCodes {
      code
      applicable
    }
  }

  fragment MoneyFragment on MoneyV2 {
    currencyCode
    amount
  }
  fragment ImageFragment on Image {
    id
    url
    altText
    width
    height
  }
`;

const ShopifyHeadlessProvider = ({ children }: Props) => {
  return (
    <HydrogenShopifyProvider
      storeDomain={storeDomain}
      storefrontToken={storefrontToken}
      storefrontApiVersion={storefrontApiVersion}
      countryIsoCode="US"
      languageIsoCode="EN"
    >
      <CartProvider cartFragment={CART_FRAGMENT}>
        {children}
      </CartProvider>
    </HydrogenShopifyProvider>
  );
};

export default ShopifyHeadlessProvider;
